/**
 * Shared pipeline-stage helpers for the candidate views.
 *
 * The job's `Pipeline.stages` (ordered by `position`) is the single source of
 * truth for which stages exist and in what order. Table and Kanban both resolve
 * candidates through here so the two views cannot drift apart.
 */
import type { TFunction } from 'i18next';
import type { Candidate, PipelineStage } from '../models/job';

/** Default-stage semantics that have completed an interview (fit scores, etc.). */
export const ASSESSED_STAGE_SEMANTICS = new Set([
  'interviewed',
  'accepted',
  'rejected',
  'hired',
]);

const DEFAULT_STAGE_LABEL_KEYS = {
  pending: 'status_tabs.pending',
  shortlisted: 'status_tabs.shortlisted',
  interviewing: 'status_tabs.interviewing',
  interviewed: 'status_tabs.interviewed',
  accepted: 'status_tabs.accepted',
  rejected: 'status_tabs.rejected',
  hired: 'status_tabs.hired',
} as const;

type DefaultStageType = keyof typeof DEFAULT_STAGE_LABEL_KEYS;

const DEFAULT_STAGE_SEMANTICS = new Set<string>(Object.keys(DEFAULT_STAGE_LABEL_KEYS));

function isDefaultStageType(stageType: string): stageType is DefaultStageType {
  return DEFAULT_STAGE_SEMANTICS.has(stageType);
}

/**
 * Display name for a stage. Untouched default stages are system-owned and
 * localized from `stageType`; custom and recruiter-renamed default stages are
 * user-owned and always shown exactly as stored.
 */
export function stageDisplayLabel(
  stage: Pick<PipelineStage, 'label' | 'isDefault' | 'stageType' | 'isLabelCustomized'>,
  t: TFunction<'job'>
): string {
  if (!stage.isDefault || stage.isLabelCustomized || !isDefaultStageType(stage.stageType)) {
    return stage.label;
  }
  return t(DEFAULT_STAGE_LABEL_KEYS[stage.stageType]);
}

/** Display name for a stage type (aggregate views), honoring the job's renamed stages. */
export function stageTypeDisplayLabel(
  stages: PipelineStage[] | undefined,
  stageType: string,
  t: TFunction<'job'>
): string {
  const stage = findStageByType(stages, stageType);
  if (stage) return stageDisplayLabel(stage, t);
  return isDefaultStageType(stageType) ? t(DEFAULT_STAGE_LABEL_KEYS[stageType]) : stageType;
}

export function isAssessedStageSemantic(
  semantic: string | undefined
): boolean {
  return Boolean(semantic && ASSESSED_STAGE_SEMANTICS.has(semantic));
}

export function isPendingStageSemantic(
  semantic: string | undefined
): boolean {
  return semantic === 'pending';
}

/** Job-defined stage (`custom` or any non-default semantic). */
export function isCustomStageSemantic(
  semantic: string | undefined
): boolean {
  return !semantic || semantic === 'custom' || !DEFAULT_STAGE_SEMANTICS.has(semantic);
}

export function candidateStageLabel(
  candidate: Pick<Candidate, 'pipelineStageLabel' | 'pipelineStageSemantic'>
): string {
  return candidate.pipelineStageLabel || candidate.pipelineStageSemantic || '';
}

/** Stages in board order. */
export function sortStagesByPosition(
  stages: PipelineStage[] | undefined
): PipelineStage[] {
  return [...(stages ?? [])].sort((a, b) => a.position - b.position);
}

/**
 * Resolve which stage a candidate sits in: explicit stage id first, then the
 * stage semantic when the id is missing or unknown.
 */
export function createStageResolver(
  stages: PipelineStage[]
): (candidate: Candidate) => string {
  const byId = new Map(stages.map((stage) => [stage.id, stage]));
  const byType = new Map<string, PipelineStage>();
  for (const stage of stages) {
    if (!byType.has(stage.stageType)) byType.set(stage.stageType, stage);
  }

  return (candidate) => {
    if (candidate.pipelineStageId && byId.has(candidate.pipelineStageId)) {
      return candidate.pipelineStageId;
    }
    const semantic = candidate.pipelineStageSemantic;
    return (
      (semantic ? byType.get(semantic)?.id : undefined) ??
      candidate.pipelineStageId ??
      ''
    );
  };
}

export function findCandidateStage(
  candidate: Candidate,
  stages: PipelineStage[] | undefined
): PipelineStage | undefined {
  if (!stages?.length) return undefined;
  const stageId = createStageResolver(stages)(candidate);
  return stages.find((stage) => stage.id === stageId);
}

/** Candidate totals per stage id, for the stage tabs. */
export function getCandidateStageCounts(
  candidates: Candidate[],
  stages: PipelineStage[]
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const stage of stages) counts[stage.id] = 0;
  if (!Array.isArray(candidates) || candidates.length === 0) return counts;

  const resolveStageId = createStageResolver(stages);
  for (const candidate of candidates) {
    const stageId = resolveStageId(candidate);
    if (!stageId) continue;
    counts[stageId] = (counts[stageId] ?? 0) + 1;
  }
  return counts;
}

/**
 * After a column/list reorder, return the single stage that moved and its new
 * 1-based position. Handles both left and right moves (dnd-kit / Sortable).
 */
export function resolveMovedStagePosition(
  previousOrder: string[],
  nextOrder: string[]
): { stageId: string; position: number } | null {
  if (previousOrder.length !== nextOrder.length || previousOrder.length === 0) {
    return null;
  }

  let first = 0;
  while (
    first < nextOrder.length &&
    previousOrder[first] === nextOrder[first]
  ) {
    first += 1;
  }
  if (first === nextOrder.length) return null;

  let last = nextOrder.length - 1;
  while (last > first && previousOrder[last] === nextOrder[last]) {
    last -= 1;
  }

  const movedLeft = nextOrder[first] === previousOrder[last];
  const index = movedLeft ? first : last;

  return { stageId: nextOrder[index], position: index + 1 };
}

/**
 * Whether entering this stage will queue a notification/email automation.
 * Requires both a configured rule and the recruiter kill-switch ON.
 */
export function stageWillNotify(
  stage: Pick<PipelineStage, 'hasAutomationRule' | 'automationEnabled'> | undefined
): boolean {
  return Boolean(stage?.hasAutomationRule && stage?.automationEnabled);
}

export function findStageByType(
  stages: PipelineStage[] | undefined,
  stageType: string
): PipelineStage | undefined {
  return stages?.find((stage) => stage.stageType === stageType);
}
